import type { AsyncState } from './Async'

/**
 * Maps a TanStack Query result onto `AsyncState`.
 *
 * Nothing outside `Async.tsx` constructed an `AsyncState` before this. M5, M6
 * and M7 each need the same mapping, so a module-local version would mean
 * three more of them or an import across a module boundary — which is the
 * three-duplicate-progress-rings failure the brief records.
 *
 * **Stale is opt-in, and deliberately not derived from TanStack.**
 * `isStale`/`dataUpdatedAt` describe *when we last fetched*, which is not what
 * the stale state tells the user. Per D20 stale is bank-sync freshness
 * reported by the server, so `asOf` is a function of the DATA — a
 * `last_synced_at` off the response — and a surface with no upstream sync
 * simply passes no `asOf` and genuinely has four states. Deriving it from
 * cache age would put a plausible, wrong date in front of the user on every
 * surface in the app.
 */
export type QueryLike<T> = {
  data: T | undefined
  isPending: boolean
  isError: boolean
  error: unknown
}

export type FromQueryOptions<T> = {
  /** Whose fault and what to do about it. `Something went wrong` is a defect
   *  wherever something specific is possible, so this takes the query error. */
  message: (error: unknown) => string
  recovery?: { label: string; onAct: () => void }
  /** Data-shaped emptiness. Omitted where a response cannot be empty. */
  isEmpty?: (data: T) => boolean
  /** The date the data is FROM. Returning `undefined` means current. */
  asOf?: (data: T) => Date | undefined
}

export function fromQuery<T>(query: QueryLike<T>, options: FromQueryOptions<T>): AsyncState<T> {
  const { data } = query

  if (data === undefined) {
    // Order matters: a pending query has no data, so `isEmpty` cannot be
    // consulted and an empty state must not flash before the first response.
    if (query.isError) {
      const state: AsyncState<T> = { status: 'error', message: options.message(query.error) }
      return options.recovery ? { ...state, recovery: options.recovery } : state
    }
    return { status: 'loading' }
  }

  // Data present and a refetch failed: keep showing it. Replacing a readable
  // list with an error box because a BACKGROUND request failed loses real
  // information the user already had.
  if (options.isEmpty?.(data)) return { status: 'empty' }

  const asOf = options.asOf?.(data)
  return asOf ? { status: 'stale', data, asOf } : { status: 'success', data }
}
